import React from "react";
import Section from "./Section";
import img1 from "../../img/Screenshot 2025-10-31 162631.png";
import img2 from "../../img/Screenshot 202.png";
import "../../CodeHeaderTutorialPage.css";

// HTML_Editors.jsx
// A single-file React component that shows step-by-step instructions
// for running HTML files in Notepad, Notepad++, and VS Code.
// NOTE: The images are shown in the chat's image carousel. If you want
// to include images inside this component, replace the placeholder URLs
// with real image URLs (or import local assets).

export default function HTML_Editors() {
  const sampleHTML = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>My First Page</title>
</head>
<body>
  <h1>Hello from HTML!</h1>
 
</body>
</html>`;

  return (
    <div className="tutorial-container">
      <div>
        <h1 className="tutorial-title">
          HTML Editors — How to write & run an HTML file
        </h1>
        <div
          style={{
            background: "linear-gradient(135deg, #fef3c7, #fde68a)",
            padding: "1rem",
            borderRadius: "12px",
            marginTop: "2rem",
            marginBottom: "2rem",
          }}
        >
          <p className="mb-4">
            Below are short, clear, step-by-step instructions for writing and
            opening a simple HTML file using <strong>Notepad</strong>,{" "}
            <strong>Notepad++</strong>, and <strong>Visual Studio Code</strong>.
            Use the sample HTML code provided to test.
          </p>
        </div>

        <Section
          title=" Sample HTML (copy & paste)"
          html={sampleHTML}
          description="  Hello from HTML!"
        />
        <section className="mb-6">
          <h1>1) Notepad (Windows built-in)</h1>
          <ol className="list-decimal ml-6">
            <li>
              Open Notepad: Start → type <em>Notepad</em> → Enter.
            </li>
            <li>Paste the sample HTML into Notepad.</li>
            <li>File → Save As…</li>
            <li>
              In the Save dialog:
              <ul className="ml-6 list-disc">
                <li>
                  Filename: <code>index.html</code> (IMPORTANT: include the{" "}
                  <code>.html</code> extension).
                </li>
                <li>
                  Save as type: <strong>All Files (*.*)</strong>.
                </li>
                <li>
                  Encoding: <strong>UTF-8</strong> (recommended).
                </li>
              </ul>
            </li>
            <li>
              Open the saved file: double-click <code>index.html</code> — your
              default browser opens and renders the page.
            </li>
            <img src={img1} className="imgEditor" />
          </ol>
          <p className="mt-2 text-sm italic">
            Tip: If the browser downloads the file instead of opening it, check
            that the filename really ends with <code>.html</code> (not{" "}
            <code>.html.txt</code>).
          </p>
        </section>

        <section className="mb-6">
          <h1>2) Notepad++ (free advanced text editor)</h1>
          <ol className="list-decimal ml-6">
            <li>Install Notepad++ from the official site, then open it.</li>
            <li>Create a new file and paste the sample HTML.</li>
            <li>
              File → Save As → name it <code>index.html</code>.
            </li>
            <li>
              To preview:
              <ul className="ml-6 list-disc">
                <li>
                  Right-click the file tab → <em>Open Containing Folder</em> →
                  double-click the <code>index.html</code> file to open in your
                  browser; or
                </li>
                <li>
                  Install a plugin (like "NppExec") to run a browser command
                  directly from Notepad++, or just open the file from Explorer.
                </li>
              </ul>
            </li>
          </ol>
          <p className="mt-2 text-sm italic">
            Notepad++ highlights HTML syntax which makes editing easier.
          </p>
        </section>

        <section className="mb-6">
          <h1>3) Visual Studio Code (VS Code) — recommended for development</h1>
          <ol className="list-decimal ml-6">
            <li>Install Visual Studio Code and open it.</li>
            <li>
              Open a folder: <code>File → Open Folder…</code> (recommended) —
              this makes Live Server and other features work properly.
            </li>
            <li>
              Create a new file in the Explorer, name it <code>index.html</code>
              , then paste the sample HTML.
            </li>
            <li>Save the file (Ctrl/Cmd+S).</li>
            <li>
              Preview options:
              <ul className="ml-6 list-disc">
                <li>
                  <strong>Open in browser:</strong> Right-click the file in
                  Explorer → <em>Reveal in File Explorer</em> and double-click
                  the file; or right-click the editor →{" "}
                  <em>Open with Default Browser</em> (some extensions add this).
                </li>
                <li>
                  <strong>Live reload (best):</strong> Install the{" "}
                  <code>Live Server</code> extension (by Ritwick Dey). After
                  installing, open <code>index.html</code> and click{" "}
                  <em>Go Live</em> in the bottom status bar — your browser opens
                  and reloads automatically when you save.
                </li>
                <li>
                  If Live Server doesn't show: make sure you opened a folder
                  (not a single file), then restart VS Code if needed.
                </li>
              </ul>
            </li>
            <img src={img2} className="imgEditor" />
          </ol>
          <p className="mt-2 text-sm italic">
            Live Server is great for quick development because it reloads the
            browser every time you save.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">
            Troubleshooting & quick checks
          </h2>
          <ul className="list-disc ml-6">
            <li>
              Make sure files are saved with <code>.html</code> extension.
            </li>
            <li>
              Open the file in a browser (Chrome, Edge, Firefox) — drag & drop{" "}
              <code>index.html</code> into the browser window to open it.
            </li>
            <li>
              If CSS or JS doesn't apply, verify relative paths (e.g.,{" "}
              <code>./style.css</code>).
            </li>
          </ul>
        </section>

        <footer className="text-sm text-gray-600 mt-6">
          <p>
            How to use these images: the chat shows several example screenshots
            for Notepad, Notepad++ and VS Code. To embed images inside this
            component, replace the placeholder URLs with direct image links (for
            example from Wikimedia commons) and include{" "}
            <code>&lt;img src="..." alt="..." /&gt;</code> where you want them
            to appear.
          </p>
        </footer>
      </div>
    </div>
  );
}
